import React, { Component } from 'react';
import * as lib from '~/dependencies';
import AudioVisualiser from './AudioVisualiser';
import { PidsWrapper, BoxPids } from './styles';

// The 20 static dashes behind the level meter. Their keys come from this list because the map
// callback of an `{ length: n }` array-like is handed `undefined` as the element for every slot -
// keying on that gave all 20 dashes the same (missing) key.
const PID_KEYS = Array.from({ length: 20 }, (_, index) => `pid-${index}`);

class MicrophoneAudioAnalyzer extends Component {
  constructor(props) {
    super(props);
    this.state = { audioData: new Uint8Array(0) };
    this.mounted = false;
    this.tick = this.tick.bind(this);
  }

  componentDidMount() {
    const { audio } = this.props;

    this.mounted = true;
    this.startAnalysing(audio);
  }

  componentDidUpdate(prevProps) {
    const { audio } = this.props;

    if (audio !== prevProps.audio) {
      // Tear the previous graph down first. Starting a second loop on top of the first left the
      // older one unreachable - `rafId` only ever held the newest - so it outlived unmount and
      // kept calling setState, which is what React reports as a leak.
      this.stopAnalysing();
      this.startAnalysing(audio);
    }
  }

  componentWillUnmount() {
    const { stopMicrophone } = this.props;

    this.mounted = false;
    this.stopAnalysing();
    stopMicrophone();
  }

  startAnalysing(audio) {
    // `audio` is null until the parent's getUserMedia resolves, and stays null when it is denied.
    if (!audio || typeof audio.getTracks !== 'function') {
      return;
    }

    this.audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    this.analyser = this.audioContext.createAnalyser();
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.source = this.audioContext.createMediaStreamSource(audio);
    this.source.connect(this.analyser);
    this.rafId = requestAnimationFrame(this.tick);
  }

  stopAnalysing() {
    cancelAnimationFrame(this.rafId);
    this.rafId = undefined;

    // Optional calls: this also runs for a mount that never got a stream, and for a stream the
    // browser refused.
    this.source?.disconnect();
    this.analyser?.disconnect();
    // Disconnecting is not enough - a context has to be closed or it keeps its audio thread, and
    // browsers only allow a handful of open contexts per page.
    Promise.resolve(this.audioContext?.close?.()).catch(() => {});

    this.source = undefined;
    this.analyser = undefined;
    this.audioContext = undefined;
  }

  tick() {
    if (!this.mounted || !this.analyser) {
      return;
    }

    this.analyser.getByteTimeDomainData(this.dataArray);
    this.setState({ audioData: this.dataArray });
    this.rafId = requestAnimationFrame(this.tick);
  }

  render() {
    const { audioData } = this.state;
    const { barColor } = this.props;
    return (
      <PidsWrapper>
        <BoxPids dashes>
          {PID_KEYS.map(pidKey => (
            <div key={pidKey} className="pid" />
          ))}
        </BoxPids>

        <AudioVisualiser audioData={audioData} barColor={barColor} />
      </PidsWrapper>
    );
  }
}

MicrophoneAudioAnalyzer.defaultProps = {
  audio: null,
  stopMicrophone: () => {},
  barColor: '#2C4F9E',
};

MicrophoneAudioAnalyzer.propTypes = {
  audio: lib.PropTypes.instanceOf(Object),
  stopMicrophone: lib.PropTypes.func,
  barColor: lib.PropTypes.string,
};

export default MicrophoneAudioAnalyzer;
